import * as Icon from "@ant-design/icons";
import { Badge, Layout, Menu, Tooltip, theme } from "antd";
import type { ItemType, MenuItemType } from "antd/es/menu/interface";
import { type IFlagsmithFeature, useFlags } from "flagsmith/react";
import { useCloseTrackerBadge } from "hooks/useCloseTrackerBadge";
import { useCompanies } from "hooks/useCompanies";
import { useHidePmCloseTabs } from "hooks/useHidePmCloseTabs";
import { usePluginAccess } from "hooks/usePluginAccess";
import {
	useMyTicketsAwaitingReply,
	useTicketsAwaitingReply,
} from "hooks/useSupportTickets";

import { useUser } from "hooks/useUser";
import { useWorkflowRuns } from "hooks/useWorkflowRuns";
import type React from "react";
import { useContext, useEffect, useRef } from "react";
import { Link, useMatch } from "react-router-dom";
import type { IUser } from "types/user.types";
import { AdminScope, hasAnyAdminAccess, hasAnyScope } from "utils/adminScopes";
import { config } from "utils/config";
import { analytics } from "../utils/analytics";
import { SIDER_MENU_INLINE_INDENT } from "./siderLayout";

export function AppMenu() {
	const { user, logout } = useUser();
	const { runs } = useWorkflowRuns();

	const myApprovalsCount = user
		? runs.filter(
				(r) =>
					r.status === "RUNNING" &&
					r.metadata?.stepAssignees &&
					Object.values(r.metadata.stepAssignees).includes(user.id),
			).length
		: 0;

	// Admin-only endpoint. Gated on the SUPPORT_TICKETS scope rather than on
	// `user.admin`, or a scoped support hire — the one person who needs this
	// badge — would never fetch it, while a scoped non-support staffer would
	// fire a request that 403s on every render.
	const ticketsAwaitingReply = useTicketsAwaitingReply(
		hasAnyScope(user, [AdminScope.SUPPORT_TICKETS]),
	);

	const match = useMatch("/*");
	const fullPath = match?.params["*"];
	const path = fullPath?.split("/")?.[0];
	const { isPluginDisabled } = usePluginAccess();
	const flags = useFlags([
		"disable_workflow_templates",
		"disable_workflows",
		"disable_support_ticket",
		"hide_dashboard",
		"hide_intercompany",
		"workflows_v2",
	]);

	// `hide_dashboard` hides the close: the entry below, and — since
	// `CloseTrackerGate` — the `/workflow` route behind it. So the badge is
	// fetched only while the close is actually reachable; counting sign-offs
	// nobody can open is a `close/my-week` request for nothing.
	// A flag Flagsmith never returns reads as `undefined`, which falls through
	// to visible — same fail-open direction as the route gate.
	const isCloseHidden = !!(flags.hide_dashboard as IFlagsmithFeature)?.enabled;
	const closeTrackerBadge = useCloseTrackerBadge(!isCloseHidden);
	// Inverse too, paired with `IntercompanyGate` on the route.
	const isIntercompanyHidden = !!(flags.hide_intercompany as IFlagsmithFeature)
		?.enabled;
	// The release switch for the close inside Project Management. ON (the
	// default) keeps the separate Close Tracker group below exactly as it is.
	const isPmCloseTabsHidden = useHidePmCloseTabs();
	const companies = useCompanies();
	const hasNoCompanies = companies.data?.length === 0;

	const isSupportDisabled = !!(
		flags.disable_support_ticket as IFlagsmithFeature
	)?.enabled;

	// Tickets support has replied to and this user has not answered. Skipped when
	// the Support item itself is flagged off — nothing would render the count.
	const myTicketsAwaitingReply = useMyTicketsAwaitingReply(
		!!user && !isSupportDisabled,
	);

	// Use useRef to track component mount state
	const isMounted = useRef(true);

	// Set isMounted to false when component unmounts
	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	// Create base menu children for workflow
	const workflowChildren: ItemType<MenuItemType>[] = [
		{
			label: <Link to="/workflow/projects">Projects</Link>,
			key: "workflow/projects",
		},
		{
			label: <Link to="/workflow/tasks">Tasks</Link>,
			key: "workflow/tasks",
		},
	];

	// Add Templates to workflowChildren if not disabled
	if (!(flags.disable_workflow_templates as IFlagsmithFeature)?.enabled) {
		workflowChildren.push({
			label: <Link to="/workflow/templates">Templates</Link>,
			key: "workflow/templates",
		});
	}

	// Add Workflows to workflowChildren if not disabled
	if (!(flags.disable_workflows as IFlagsmithFeature)?.enabled) {
		workflowChildren.push({
			label: <Link to="/workflow/workflows">Workflows</Link>,
			key: "workflow/workflows",
		});
	}

	// With the close inside Project Management there is one group, in the tab
	// bar's order, then Inter-company. The close attention count sits on the
	// Close board item; the approvals count stays on the group's icon.
	const projectManagementChildren: ItemType<MenuItemType>[] =
		isPmCloseTabsHidden
			? workflowChildren
			: [
					{
						label: <Link to="/workflow/projects">Projects</Link>,
						key: "workflow/projects",
					},
					{
						label: <Link to="/workflow/tasks">Tasks</Link>,
						key: "workflow/tasks",
					},
					...(isCloseHidden
						? []
						: [
								{
									// A bare "3" means nothing read aloud: the link says what
									// the count is, and the badge is the sighted copy of it.
									label: (
										<Link
											to="/workflow"
											aria-label={
												closeTrackerBadge > 0
													? `Close board, ${closeTrackerBadge} waiting for you`
													: undefined
											}
										>
											Close board
										</Link>
									),
									key: CLOSE_BOARD_KEY,
									extra: (
										<span aria-hidden="true">
											<Badge count={closeTrackerBadge} size="small" />
										</span>
									),
								},
							]),
					// Templates holds Close (gone with `hide_dashboard`) and Project
					// (gone with `disable_workflow_templates`): no item when both are.
					...(isCloseHidden &&
					(flags.disable_workflow_templates as IFlagsmithFeature)?.enabled
						? []
						: [
								{
									label: <Link to="/workflow/templates">Templates</Link>,
									key: "workflow/templates",
								},
							]),
					...((flags.disable_workflows as IFlagsmithFeature)?.enabled
						? []
						: [
								{
									label: <Link to="/workflow/workflows">Workflows</Link>,
									key: "workflow/workflows",
								},
							]),
					...(isIntercompanyHidden
						? []
						: [
								{
									label: <Link to="/firm/intercompany">Inter-company</Link>,
									key: "firm-intercompany",
								},
							]),
				];

	// Create menu items dynamically to handle feature flags
	let currentMenuItems: ItemType<MenuItemType>[] = [
		{
			label: (
				<Link
					to="/"
					style={{
						display: "block",
						whiteSpace: "normal",
						lineHeight: 1.2,
					}}
				>
					All Companies
				</Link>
			),
			key: "companies",
			icon: <Icon.ProductOutlined />,
		},
		// EQ-2362: directly below All Companies, directly above Project Management.
		...(!isCloseHidden && isPmCloseTabsHidden
			? [
					{
						// EQ-2311: a group header, not a link. The tracker itself is
						// reached through the Dashboard child below.
						label: (
							<div style={{ whiteSpace: "normal", lineHeight: 1.2 }}>
								Close Tracker
							</div>
						),
						key: "firm",
						// Badged on the icon so the count survives a collapsed sidebar.
						// The badge used to live on a separate flag-gated "Close tracker"
						// entry; since `/firm` redirects to `/workflow` (R1.2) both led to
						// the same page, so the duplicate was removed and its badge moved
						// here.
						icon: (
							<Badge count={closeTrackerBadge} size="small" offset={[4, -2]}>
								<Icon.BankOutlined />
							</Badge>
						),
						children: [
							{
								label: (
									<Link
										to="/workflow"
										// The company menu above has its own "Dashboard"; this
										// name keeps the two links distinct for assistive tech.
										aria-label="Close Tracker dashboard"
										style={{
											display: "block",
											whiteSpace: "normal",
											lineHeight: 1.2,
										}}
									>
										Dashboard
									</Link>
								),
								key: "firm-dashboard",
								icon: <Icon.DashboardOutlined />,
							},
							...(isIntercompanyHidden
								? []
								: [
										{
											label: (
												<Link
													to="/firm/intercompany"
													style={{
														display: "block",
														whiteSpace: "normal",
														lineHeight: 1.2,
													}}
												>
													Inter-company
												</Link>
											),
											key: "firm-intercompany",
											icon: <Icon.SwapOutlined />,
										},
									]),
							{
								// EQ-2317. Keyed by its path so `selected` below resolves
								// it like any other item. Not the Project Management
								// "Templates" above: those are project templates.
								label: (
									<Link
										to="/workflow/close/templates"
										aria-label="Close templates"
										style={{
											display: "block",
											whiteSpace: "normal",
											lineHeight: 1.2,
										}}
									>
										Templates
									</Link>
								),
								key: "workflow/close/templates",
								icon: <Icon.ProfileOutlined />,
							},
						],
					},
				]
			: []),
		{
			label: <div style={menuLinkStyle}>Project Management</div>,
			key: "workflow",
			icon: (
				<Badge count={myApprovalsCount} size="small" offset={[4, -2]}>
					<Icon.CarryOutOutlined />
				</Badge>
			),
			children: projectManagementChildren,
		},
		{
			label: (
				<Link to="/account/billing" style={menuLinkStyle}>
					Account
				</Link>
			),
			key: "account",
			icon: <Icon.UserOutlined />,
		},
		{
			label: (
				<Link to="/team" style={menuLinkStyle}>
					Team Management
				</Link>
			),
			key: "team",
			icon: <Icon.TeamOutlined />,
		},
		{
			label: (
				<Link
					to="/document-center"
					style={{
						display: "block",
						whiteSpace: "normal",
						lineHeight: 1.2,
					}}
				>
					Document Center
				</Link>
			),
			key: "document-center",
			icon: <Icon.FileTextOutlined />,
		},
		{
			label: (
				<Link
					to="/support"
					style={{
						display: "block",
						whiteSpace: "normal",
						lineHeight: 1.2,
					}}
				>
					Support
				</Link>
			),
			key: "support",
			// Badged on the icon only, so the count survives a collapsed sidebar
			// where the label is hidden.
			icon: (
				<Badge count={myTicketsAwaitingReply} size="small" offset={[4, -2]}>
					<Icon.CustomerServiceOutlined />
				</Badge>
			),
		},
		{
			label: (
				<Link
					to="/help"
					style={{
						display: "block",
						whiteSpace: "normal",
						lineHeight: 1.2,
					}}
				>
					Help
				</Link>
			),
			key: "help",
			icon: <Icon.InfoCircleOutlined />,
		},
		{ type: "divider" } as any,
		{
			label: "Logout",
			key: "signout",
			icon: <Icon.LogoutOutlined />,
			danger: true,
			onClick: () => logout(),
		},
	];

	// Remove Support menu item if disabled by feature flag
	if ((flags.disable_support_ticket as IFlagsmithFeature)?.enabled) {
		currentMenuItems = currentMenuItems.filter(
			(item) => item?.key !== "support",
		);
	}

	// Add download plugin option if not disabled
	if (!isPluginDisabled) {
		currentMenuItems.splice(-1, 0, {
			label: (
				<Tooltip title="Our plug-in is supported on Chrome, Edge, or Brave. Please use one of these browsers for optimal functionality.">
					<a
						href={config.links.downloadPlugin}
						target="_blank"
						rel="noreferrer"
					>
						Download Plug-in
					</a>
				</Tooltip>
			),
			key: "download",
			icon: <Icon.DownloadOutlined />,
		});
	}

	// Add File Converter for users without companies, right after Account.
	// Found by key: the Close Tracker above it comes and goes with a flag.
	if (hasNoCompanies) {
		const afterAccount =
			currentMenuItems.findIndex((item) => item?.key === "account") + 1;
		currentMenuItems.splice(afterAccount, 0, {
			label: (
				<Link
					to="/file-converter"
					style={{
						display: "block",
						whiteSpace: "normal",
						lineHeight: 1.2,
					}}
				>
					File Converter
				</Link>
			),
			key: "file-converter",
			icon: <Icon.FileTextOutlined />,
		});
	}

	// The admin console appears for anyone holding at least one scope, not just
	// `admin: true`. Without this a scoped hire gets NO admin nav at all while
	// their API calls succeed — the console simply does not exist for them,
	// even though `Routes.tsx` registers every admin page unconditionally and
	// they can still reach one by typing its URL.
	const showAdmin = hasAnyAdminAccess(user);
	const items = showAdmin
		? [...buildAdminItems(ticketsAwaitingReply, user), ...currentMenuItems]
		: currentMenuItems;
	const home = showAdmin ? items[1]?.key : items[0]?.key;
	const flatItems = items.flatMap((item: any) =>
		item?.children ? item?.children : item,
	);
	// 🪤 The close tracker lives at `/workflow`, but `flatItems` above replaces
	// any parent that HAS children with those children, and "workflow" is the
	// Project Management group, so no key ever matches the tracker's path and
	// NOTHING would highlight on it. Its entry is the Dashboard child
	// ("firm-dashboard") of the Close Tracker group.
	//
	// Resolve the close tracker's own two routes explicitly. Everything else
	// under /workflow is Project Management and still resolves by key, so
	// `/workflow/projects` keeps highlighting its own tab rather than the close.
	// `workflow/close/*` is a client's close (the old address), part of the
	// tracker; it highlighted nothing. So did Inter-company: its key "firm-intercompany"
	// never equals its path "firm/intercompany" (/qa r9 E-8).
	const isCloseTracker =
		fullPath === "workflow" ||
		Boolean(fullPath?.startsWith("workflow/client")) ||
		(Boolean(fullPath?.startsWith("workflow/close/")) &&
			fullPath !== "workflow/close/templates");
	const isIntercompany = fullPath === "firm/intercompany";
	// Inside Project Management every close address is the board's, including
	// a client's close, close settings and the old My week `?view=week`.
	const closeKey = isPmCloseTabsHidden ? "firm-dashboard" : CLOSE_BOARD_KEY;
	const selected = isCloseTracker
		? closeKey
		: isIntercompany
			? "firm-intercompany"
			: path
				? flatItems.find(
						(item) => path === "" + item?.key || fullPath === "" + item?.key,
					)?.key || ""
				: home;

	// The Sider's railed state (collapsed or narrow), as antd's Menu reads it.
	const isSiderCollapsed = Boolean(
		useContext(Layout._InternalSiderContext).siderCollapsed,
	);

	// Only identify user if component is still mounted
	useEffect(() => {
		if (user && isMounted.current) {
			analytics.identify(String(user.id), {
				firstName: user?.firstName,
				lastName: user?.lastName,
				email: user?.email,
			});
		}
	}, [user]);

	// Open the group that holds the selected page on load, so a direct load or
	// reload of /workflow shows its highlighted Dashboard (/qa r12 A-1). Only
	// the initial value: after that the user opens and closes groups freely.
	// Not on a railed sider, where an open group is a popup left hanging open.
	const selectedGroup = isSiderCollapsed
		? undefined
		: items.find((item: any) =>
				item?.children?.some((child: any) => child?.key === selected),
			)?.key;

	return (
		<Menu
			mode="inline"
			inlineIndent={SIDER_MENU_INLINE_INDENT}
			items={items}
			selectedKeys={[selected as string]}
			defaultOpenKeys={selectedGroup ? [String(selectedGroup)] : undefined}
			style={menuStyle}
		/>
	);
}

/**
 * Key of the close item inside the Project Management group. Not a path: the
 * board lives at `/workflow`, which is the group's own key.
 */
const CLOSE_BOARD_KEY = "pm-close-board";

/**
 * The admin console menu, gated per cluster.
 *
 * Each entry names the scope(s) that make its pages usable. An entry whose
 * scope the viewer lacks is omitted rather than shown-and-disabled: a visible
 * dead link in an authorization UI reads as a bug, and the API would 403 every
 * call behind it anyway.
 *
 * 🚨 `Machine Learning` is gated on the LEGACY BOOLEAN ONLY, deliberately.
 * That tab talks to the ML service directly with an `X-Api-Key`
 * (`pages/admin/machine-learning/services/mlAdminApi.ts`) and never touches
 * equility-api, so `AdminGuard` never runs on it and none of its actions —
 * including `data/purge` and `models/rollback` — reach the staff audit log or
 * carry a user identity. There is no scope that could honestly gate it, so no
 * scoped hire gets it. See `docs/staff-scopes.md`.
 */
export function buildAdminItems(
	ticketsAwaitingReply: number,
	user: IUser | undefined | null,
): ItemType<MenuItemType>[] {
	const children: ItemType<MenuItemType>[] = [];

	if (hasAnyScope(user, [AdminScope.USERS_READ, AdminScope.USERS_WRITE])) {
		children.push({
			label: <Link to="/admin/users">Users</Link>,
			key: "admin/users",
		});
	}

	// These are exactly the two scopes `GET admin/staff-scopes` accepts, and the
	// pairing is deliberate: seeing who holds which scope is a USERS_READ
	// concern, while STAFF_ACCESS_WRITE is the authority to change it — and a
	// holder of the second who lacked the first could open the page and get a
	// 403 on its only read. Granting is reflected in the page's disabled
	// controls rather than by hiding the cluster, so a reviewer with USERS_READ
	// alone can still audit the roster.
	if (
		hasAnyScope(user, [AdminScope.USERS_READ, AdminScope.STAFF_ACCESS_WRITE])
	) {
		children.push({
			label: <Link to="/admin/staff-access">Staff Access</Link>,
			key: "admin/staff-access",
		});
	}

	// Scoped to SUPPORT_SESSIONS rather than blanket admin: opening a session
	// against a customer's books is exactly the capability the staff-scope work
	// exists to separate from "is an admin of anything".
	if (hasAnyScope(user, [AdminScope.SUPPORT_SESSIONS])) {
		children.push({
			label: <Link to="/admin/support-access">Support Access</Link>,
			key: "admin/support-access",
		});

		// Same scope as Support Access above, and deliberately so: `GET
		// support-access/grants` already lists every live session across all
		// customers, and the Access Log is that list's historical half. Gating
		// the ledger tighter than the live view it records would hide the
		// evidence from the people holding the capability it evidences.
		children.push({
			label: <Link to="/admin/access-log">Access Log</Link>,
			key: "admin/access-log",
		});
	}

	if (
		hasAnyScope(user, [
			AdminScope.COMPANIES_READ,
			AdminScope.COMPANIES_EXPORT,
			AdminScope.MEMBERSHIPS,
		])
	) {
		children.push({
			label: <Link to="/admin/companies">Companies</Link>,
			key: "admin/companies",
		});
	}

	if (hasAnyScope(user, [AdminScope.COMPANIES_READ])) {
		children.push(
			{
				label: <Link to="/admin/rules">Rules</Link>,
				key: "admin/rules",
			},
			{
				label: <Link to="/admin/services">Services</Link>,
				key: "admin/services",
			},
		);
	}

	// Legacy boolean only — see the note above this function.
	if (user?.admin) {
		children.push({
			label: <Link to="/admin/machine-learning">Machine Learning</Link>,
			key: "admin/machine-learning",
		});
	}

	if (
		hasAnyScope(user, [
			AdminScope.SUPPORT_SESSIONS,
			AdminScope.AI_ANALYTICS,
			AdminScope.AI_CONFIG,
		])
	) {
		children.push({
			label: <Link to="/admin/chat">Chat</Link>,
			key: "admin/chat",
		});
	}

	// Customers who asked the assistant for a person. Own entry so staff on
	// support duty reach the queue in one click.
	if (hasAnyScope(user, [AdminScope.SUPPORT_SESSIONS])) {
		children.push({
			label: <Link to="/admin/chat/live">Live chat</Link>,
			key: "admin/chat/live",
		});
	}

	if (hasAnyScope(user, [AdminScope.SUPPORT_TICKETS])) {
		children.push({
			label: (
				<Link to="/admin/support-tickets">
					Support Tickets
					<Badge
						count={ticketsAwaitingReply}
						size="small"
						style={{ marginLeft: 8 }}
					/>
				</Link>
			),
			key: "admin/support-tickets",
		});
	}

	if (hasAnyScope(user, [AdminScope.FILES])) {
		children.push({
			label: <Link to="/admin/file-converter">File Converter</Link>,
			key: "admin/file-converter",
		});
	}

	if (hasAnyScope(user, [AdminScope.BILLING])) {
		children.push({
			label: <Link to="/admin/credit-packages">Credit Packages</Link>,
			key: "admin/credit-packages",
		});
	}

	if (children.length === 0) return [];

	return [
		{
			label: (
				<div
					style={{
						whiteSpace: "normal",
						lineHeight: 1.2,
					}}
				>
					Admin
				</div>
			),
			key: "admin",
			icon: (
				<Badge count={ticketsAwaitingReply} size="small" offset={[4, -2]}>
					<Icon.AlertOutlined />
				</Badge>
			),
			children,
		},
	];
}

/** Lets a long nav label wrap instead of being clipped by a narrow sidebar. */
const menuLinkStyle: React.CSSProperties = {
	display: "block",
	whiteSpace: "normal",
	lineHeight: 1.2,
};

const menuStyle: React.CSSProperties = {
	borderRadius: theme.getDesignToken().borderRadius,
};
